import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    DialogContent,
    TextField,
    DialogTitle,
    DialogActions,
    FormControl,
    InputLabel,
    MenuItem,
    FormHelperText,
    Autocomplete,
    CircularProgress,
    Tooltip,
    Dialog,
    Select,
} from '@mui/material';
import PageviewIcon from '@mui/icons-material/Pageview';
import colors from 'assets/theme/base/colors';
import pxToRem from 'assets/theme/function/px-to-rem';
import rgba from 'assets/theme/function/rgba';
import DialogIconClose from 'component/dialog-icon-close';
import ViewProduct from 'component/view-product';
import SuccessSnackbarEffect from 'component/success-snackbar';
import { Controller, useForm } from 'react-hook-form';
import { useAppDispatch } from 'hooks/redux';
import { openSnackbar } from 'store/slices/snackbar/snackbar-slice';
import { EditItemRequest } from 'types/toont-tint/tool-tint-item';
import {
    useEditItemToonTintMutation,
    useGetItemIdDataQuery,
} from 'store/slices/toon-tint/toon-tint-item';
import { useProductTitleSearchListQuery } from 'store/slices/toon-tint/toon-tint';

interface Props {
    setOpenEditCategoryDialog: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: () => void;
    itemId: string;
}

const defaultSelected = [
    { label: 'True', value: 'true' },
    { label: 'False', value: 'false' },
];

const EditItem: React.FC<Props> = ({
    setOpenEditCategoryDialog,
    refetch,
    itemId,
}) => {
    const dispatch = useAppDispatch();
    const formRef = useRef<HTMLFormElement>(null);

    const [searchProduct, setSearchProduct] = useState('');
    const [viewProduct, setViewProduct] = useState('');
    const [openViewProductDialog, setOpenViewProductDialog] = useState(false);

    const { data, isSuccess: dataIsSuccess } = useGetItemIdDataQuery(itemId, {
        refetchOnMountOrArgChange: true,
    });

    const { data: dataProductToonTint, isFetching } =
        useProductTitleSearchListQuery({
            productType: 'toon-tint',
            searchText: searchProduct,
        });

    const [editItem, { error, isError, isSuccess, isLoading }] =
        useEditItemToonTintMutation();

    const {
        handleSubmit,
        control,
        formState: { errors },
        reset,
    } = useForm<EditItemRequest>({
        defaultValues: {
            itemId: undefined,
            itemPosition: undefined,
            productId: undefined,
            isDefault: undefined,
        },
    });

    useEffect(() => {
        if (dataIsSuccess && data) {
            reset({
                itemPosition: data.itemPosition ?? '',
                productId: data.productId ?? '',
                isDefault:
                    data.isDefault !== undefined ? String(data.isDefault) : '',
            });

            if (data.productId && data.productTitle) {
                setViewProduct(data.productId);
            }
        }
    }, [dataIsSuccess]);

    useEffect(() => {
        if (isError && error && 'data' in error) {
            dispatch(
                openSnackbar({
                    message: error.data.message,
                    severity: 'error',
                    open: true,
                    position: { vertical: 'top', horizontal: 'right' },
                }),
            );
        }
    }, [isError, error, dispatch]);

    useEffect(() => {
        if (isSuccess) {
            setOpenEditCategoryDialog(false);
            refetch();
        }
    }, [isSuccess, refetch, setOpenEditCategoryDialog]);

    const closeModal = () => {
        setOpenEditCategoryDialog(false);
        reset();
    };

    const onSubmit = (values: EditItemRequest) => {
        editItem({
            itemPosition: Number(values.itemPosition),
            isDefault:
                values.isDefault === 'true'
                    ? true
                    : values.isDefault === 'false'
                      ? false
                      : undefined,
            productId: values.productId,
            itemId,
        });
    };

    return (
        <>
            <Box
                display="flex"
                justifyContent="center"
                bgcolor={rgba(colors.primary.main, 0.5)}
            >
                <DialogTitle textTransform="uppercase">EDIT ITEM</DialogTitle>
                <DialogIconClose handleClose={closeModal} />
            </Box>

            <DialogContent>
                {dataIsSuccess && (
                    <Box
                        ref={formRef}
                        component="form"
                        onSubmit={handleSubmit(onSubmit)}
                    >
                        <Box display="flex" pt={pxToRem(15)} gap={pxToRem(10)}>
                            <Controller
                                name="itemPosition"
                                control={control}
                                rules={{
                                    required: 'Position Category is required',
                                }}
                                render={({
                                    field: { name, value = '', onChange },
                                }) => (
                                    <TextField
                                        fullWidth
                                        name={name}
                                        value={value}
                                        onChange={onChange}
                                        label="Position Category"
                                        error={Boolean(errors[name])}
                                        helperText={
                                            errors[name]?.message?.toString() ||
                                            ''
                                        }
                                    />
                                )}
                            />

                            <FormControl
                                fullWidth
                                error={Boolean(errors?.isDefault)}
                            >
                                <InputLabel>Selected Default</InputLabel>
                                <Controller
                                    name="isDefault"
                                    control={control}
                                    render={({
                                        field: { name, value, onChange },
                                    }) => (
                                        <Select
                                            name={name}
                                            value={value || ''}
                                            onChange={onChange}
                                            label="Selected Default"
                                        >
                                            {defaultSelected.map((status) => (
                                                <MenuItem
                                                    key={status.value}
                                                    value={status.value}
                                                >
                                                    {status.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                                {errors?.isDefault && (
                                    <FormHelperText>
                                        {errors.isDefault.message?.toString()}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        </Box>

                        <Box
                            display="flex"
                            alignItems="center"
                            pt={pxToRem(15)}
                            gap={pxToRem(10)}
                        >
                            <Controller
                                name="productId"
                                control={control}
                                render={({
                                    field: { onChange, value },
                                    fieldState,
                                }) => {
                                    const selected =
                                        dataProductToonTint?.find(
                                            (p) => p.id === value,
                                        ) ||
                                        (value && data?.productTitle
                                            ? {
                                                  id: value,
                                                  title: data.productTitle,
                                              }
                                            : null);

                                    return (
                                        <Autocomplete
                                            sx={{ width: '50%' }}
                                            options={dataProductToonTint ?? []}
                                            loading={isFetching}
                                            value={selected}
                                            onChange={(_, newValue) => {
                                                onChange(newValue?.id || '');
                                                setSearchProduct(
                                                    newValue?.id || '',
                                                );
                                                setViewProduct(
                                                    newValue?.id || '',
                                                );
                                            }}
                                            getOptionLabel={(option) =>
                                                option?.title || ''
                                            }
                                            isOptionEqualToValue={(
                                                option,
                                                val,
                                            ) => option.id === val?.id}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Product"
                                                    variant="outlined"
                                                    fullWidth
                                                    error={!!fieldState.error}
                                                    helperText={
                                                        fieldState.error
                                                            ?.message
                                                    }
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {isFetching && (
                                                                    <CircularProgress
                                                                        size={
                                                                            20
                                                                        }
                                                                    />
                                                                )}
                                                                {
                                                                    params
                                                                        .InputProps
                                                                        .endAdornment
                                                                }
                                                            </>
                                                        ),
                                                    }}
                                                />
                                            )}
                                        />
                                    );
                                }}
                            />
                            {viewProduct && (
                                <Tooltip title="View Product">
                                    <PageviewIcon
                                        fontSize="large"
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() =>
                                            setOpenViewProductDialog(true)
                                        }
                                    />
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button
                    sx={{ width: '50%' }}
                    onClick={() => formRef.current?.requestSubmit()}
                    variant="contained"
                    disabled={isLoading}
                >
                    Edit Item
                </Button>
                <Button
                    sx={{ width: '50%', bgcolor: colors.action.menuSelect }}
                    onClick={closeModal}
                    variant="contained"
                >
                    Cancel
                </Button>
            </DialogActions>

            <Dialog
                open={openViewProductDialog}
                onClose={() => setOpenViewProductDialog(false)}
                slotProps={{
                    paper: {
                        sx: {
                            width: '800px',
                            minHeight: '600px',
                            maxWidth: '90%',
                        },
                    },
                }}
            >
                <ViewProduct
                    setOpenViewProductDialog={setOpenViewProductDialog}
                    viewProduct={viewProduct}
                />
            </Dialog>

            <SuccessSnackbarEffect
                isSuccess={isSuccess}
                message="Successfully Edit an Card!"
            />
        </>
    );
};

export default EditItem;
